/**
 * Contracts for module "audit-remediation" (Advanced Growth Audit + Remediation). Owner: audit-remediation (track F0).
 * Spec: docs/specs/advanced-audit-remediation.md. Import as '@qm/contracts/audit-remediation'.
 *
 * Binding rules encoded here:
 * - Every finding carries evidence items (check id, pages, quote/count, timestamp), a 0-100 priority, a roadmap slot
 *   and a resolvability (auto | assisted | manual | expert).
 * - Lifecycle statuses are moved only along FINDING_TRANSITIONS; `dismissed` (and `accepted_risk`) need a reason.
 * - Remediation is approve-first: proposed -> approved -> applying -> applied -> verified | failed; rollback is
 *   approve-first as well (PROPOSAL_TRANSITIONS).
 * - ProviderConnection never carries a secret or a vault reference; credentials stay in the integrations vault.
 */
import { z } from 'zod';
import { IsoDateTimeSchema } from './common';
import {
  CompetitorAnalysisSchema, CoverageSchema, EffortSchema, FixOwnerSchema, GradeSchema, LocalizedTextSchema, SeveritySchema, SIGNAL_IDS, SignalIdSchema,
  type Severity, type SignalId,
} from './audit-analysis';

export const AUDIT_REMEDIATION_VERSION = 1 as const;

// ---------- ids ----------
/** Ids used in paths: job ids, finding ids, proposal ids. Finding ids from the analyst are lowercase dotted ids. */
export const RefIdSchema = z.string().trim().min(1).max(80).regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/, 'invalid id');
export const CheckIdSchema = z.string().trim().min(1).max(120);
/** `<provider>.<action>` e.g. `wordpress.meta_title.set`, `gbp.hours.update`. */
export const ActionKindSchema = z.string().min(3).max(80).regex(/^[a-z][a-z0-9_-]*(\.[a-z0-9_-]+)+$/, 'use <provider>.<action>');
export type ActionKind = z.infer<typeof ActionKindSchema>;

// ---------- enums ----------
export const FINDING_SOURCES = ['ai', 'rubric', 'health', 'security', 'monitor'] as const;
export const FindingSourceSchema = z.enum(FINDING_SOURCES);
export type FindingSource = z.infer<typeof FindingSourceSchema>;

export const FINDING_STATUSES = [
  'open', 'in_progress', 'awaiting_approval', 'applying', 'applied', 'verified', 'dismissed', 'accepted_risk', 'failed', 'regressed',
] as const;
export const FindingStatusSchema = z.enum(FINDING_STATUSES);
export type FindingStatus = z.infer<typeof FindingStatusSchema>;

/** Statuses a customer may set by hand (PATCH status). The rest are driven by the engine or monitoring. */
export const CUSTOMER_SETTABLE_STATUSES = ['open', 'in_progress', 'dismissed', 'accepted_risk'] as const satisfies readonly FindingStatus[];
export const CustomerSettableStatusSchema = z.enum(CUSTOMER_SETTABLE_STATUSES);
/** Statuses that require a written reason. */
export const REASON_REQUIRED_STATUSES: readonly FindingStatus[] = ['dismissed', 'accepted_risk'];
/** Customer statuses only owners/admins may set (accepting or dismissing a risk is a business decision). */
export const OWNER_ONLY_STATUSES: readonly FindingStatus[] = ['dismissed', 'accepted_risk'];

export const FINDING_ACTORS = ['customer', 'engine', 'monitor'] as const;
export type FindingActor = (typeof FINDING_ACTORS)[number];

/**
 * Allowed finding transitions per actor. `customer` = PATCH status; `engine` = remediation engine (T6);
 * `monitor` = continuous monitoring (T11). A transition to the same status is never allowed.
 */
export const FINDING_TRANSITIONS: Record<FindingActor, Partial<Record<FindingStatus, readonly FindingStatus[]>>> = {
  customer: {
    open: ['in_progress', 'dismissed', 'accepted_risk'],
    in_progress: ['open', 'dismissed', 'accepted_risk'],
    applied: ['open', 'in_progress', 'dismissed', 'accepted_risk'],
    verified: ['open', 'in_progress'],
    dismissed: ['open', 'in_progress', 'accepted_risk'],
    accepted_risk: ['open', 'in_progress', 'dismissed'],
    failed: ['open', 'in_progress', 'dismissed', 'accepted_risk'],
    regressed: ['open', 'in_progress', 'dismissed', 'accepted_risk'],
    // awaiting_approval / applying: decided by the engine (approve, reject, expire), never by hand
  },
  engine: {
    open: ['awaiting_approval'],
    in_progress: ['awaiting_approval'],
    failed: ['awaiting_approval'],
    regressed: ['awaiting_approval'],
    awaiting_approval: ['applying', 'open'], // open = rejected / expired
    applying: ['applied', 'failed'],
    applied: ['verified', 'failed', 'open'], // open = rolled back
    verified: ['open'], // rolled back
  },
  monitor: {
    applied: ['regressed', 'verified'],
    verified: ['regressed'],
    dismissed: ['regressed'],
    accepted_risk: ['regressed'],
    regressed: ['verified'],
  },
};

export function canTransitionFinding(from: FindingStatus, to: FindingStatus, actor: FindingActor): boolean {
  if (from === to) return false;
  return FINDING_TRANSITIONS[actor][from]?.includes(to) ?? false;
}

export const RESOLVABILITY_MODES = ['auto', 'assisted', 'manual', 'expert'] as const;
export const ResolvabilityModeSchema = z.enum(RESOLVABILITY_MODES);
export type ResolvabilityMode = z.infer<typeof ResolvabilityModeSchema>;

export const REMEDIATION_PROVIDERS = ['wordpress', 'gbp', 'cloudflare', 'meta'] as const;
export const RemediationProviderIdSchema = z.enum(REMEDIATION_PROVIDERS);
export type RemediationProviderId = z.infer<typeof RemediationProviderIdSchema>;

export const ResolvabilitySchema = z.object({
  mode: ResolvabilityModeSchema,
  provider: RemediationProviderIdSchema.optional(),
  actionKind: ActionKindSchema.optional(),
}).superRefine((r, ctx) => {
  if (r.mode === 'auto' && (!r.provider || !r.actionKind)) ctx.addIssue({ code: 'custom', path: ['provider'], message: 'auto needs provider and actionKind' });
});
export type Resolvability = z.infer<typeof ResolvabilitySchema>;

// ---------- cross-app check catalogue (integrator) ----------
// qm-audit emits these check ids (T1 `ADV-*`, T2 `SEC-ADV-*`); qm-api providers map them to action kinds. The two apps
// never import each other, so the ids, their non-auto default mode and the provider action able to fix them live here.
// Tests on both sides assert their tables against these (apps/audit checks-advanced + security, apps/api providers).

/** T1 advanced (non-security) checks, in emission order. */
export const ADVANCED_CHECK_IDS = [
  'ADV-TRACKING', 'ADV-TENURE-CLAIM', 'ADV-LEGAL-TEMPLATE', 'ADV-SERVICE-PAGES', 'ADV-DOORWAY-PAGES', 'ADV-SOCIAL-HIDDEN',
  'ADV-HEALTH-AD-CLAIMS', 'ADV-A11Y-ALT', 'ADV-A11Y-LABELS', 'ADV-A11Y-LANG', 'ADV-A11Y-LINK-NAMES', 'ADV-LLMS-TXT',
] as const;
export type AdvancedCheckId = (typeof ADVANCED_CHECK_IDS)[number];

/** T2 deep security checks (passive), in catalogue order. */
export const SECURITY_ADV_CHECK_IDS = [
  'SEC-ADV-HTTPS-REDIRECT', 'SEC-ADV-HSTS', 'SEC-ADV-CSP', 'SEC-ADV-FRAME', 'SEC-ADV-NOSNIFF', 'SEC-ADV-REFERRER', 'SEC-ADV-PERMISSIONS',
  'SEC-ADV-COOKIES', 'SEC-ADV-VERSION-HEADERS', 'SEC-ADV-MIXED-CONTENT', 'SEC-ADV-TLS-HTTPS', 'SEC-ADV-TLS-CERT', 'SEC-ADV-WP-VERSION',
  'SEC-ADV-WP-USERS', 'SEC-ADV-WP-AUTHOR-SCAN', 'SEC-ADV-WP-XMLRPC', 'SEC-ADV-WP-UPLOADS-LISTING', 'SEC-ADV-WP-DEBUG-LOG', 'SEC-ADV-WP-VULN',
  'SEC-ADV-FILE-ENV', 'SEC-ADV-FILE-GIT', 'SEC-ADV-FILE-WPCONFIG-BAK', 'SEC-ADV-FILE-BACKUP-ZIP', 'SEC-ADV-FILE-PHPINFO',
  'SEC-ADV-FILE-SERVER-STATUS', 'SEC-ADV-FILE-DS-STORE', 'SEC-ADV-SPF', 'SEC-ADV-DMARC', 'SEC-ADV-DKIM', 'SEC-ADV-MTA-STS', 'SEC-ADV-DNSSEC',
  'SEC-ADV-CAA', 'SEC-ADV-SAFE-BROWSING', 'SEC-ADV-BREACH',
] as const;
export type SecurityAdvCheckId = (typeof SECURITY_ADV_CHECK_IDS)[number];

export type NonAutoMode = Exclude<ResolvabilityMode, 'auto'>;

/** Default mode when no provider is connected (a check with an auto action below is `assisted`: we generate the change). */
export const AUDIT_CHECK_DEFAULT_MODES: Readonly<Record<AdvancedCheckId | SecurityAdvCheckId, NonAutoMode>> = {
  'ADV-TRACKING': 'manual',
  'ADV-TENURE-CLAIM': 'manual',
  'ADV-LEGAL-TEMPLATE': 'assisted',
  'ADV-SERVICE-PAGES': 'manual',
  'ADV-DOORWAY-PAGES': 'manual',
  'ADV-SOCIAL-HIDDEN': 'manual',
  'ADV-HEALTH-AD-CLAIMS': 'expert',
  'ADV-A11Y-ALT': 'assisted',
  'ADV-A11Y-LABELS': 'manual',
  'ADV-A11Y-LANG': 'manual',
  'ADV-A11Y-LINK-NAMES': 'manual',
  'ADV-LLMS-TXT': 'assisted',
  'SEC-ADV-HTTPS-REDIRECT': 'assisted',
  'SEC-ADV-HSTS': 'assisted',
  'SEC-ADV-CSP': 'assisted',
  'SEC-ADV-FRAME': 'assisted',
  'SEC-ADV-NOSNIFF': 'assisted',
  'SEC-ADV-REFERRER': 'assisted',
  'SEC-ADV-PERMISSIONS': 'assisted',
  'SEC-ADV-COOKIES': 'manual',
  'SEC-ADV-VERSION-HEADERS': 'manual',
  'SEC-ADV-MIXED-CONTENT': 'assisted',
  'SEC-ADV-TLS-HTTPS': 'manual',
  'SEC-ADV-TLS-CERT': 'manual',
  'SEC-ADV-WP-VERSION': 'assisted',
  'SEC-ADV-WP-USERS': 'assisted',
  'SEC-ADV-WP-AUTHOR-SCAN': 'assisted',
  'SEC-ADV-WP-XMLRPC': 'assisted',
  'SEC-ADV-WP-UPLOADS-LISTING': 'manual',
  'SEC-ADV-WP-DEBUG-LOG': 'manual',
  'SEC-ADV-WP-VULN': 'manual',
  'SEC-ADV-FILE-ENV': 'manual',
  'SEC-ADV-FILE-GIT': 'manual',
  'SEC-ADV-FILE-WPCONFIG-BAK': 'manual',
  'SEC-ADV-FILE-BACKUP-ZIP': 'manual',
  'SEC-ADV-FILE-PHPINFO': 'manual',
  'SEC-ADV-FILE-SERVER-STATUS': 'manual',
  'SEC-ADV-FILE-DS-STORE': 'manual',
  'SEC-ADV-SPF': 'assisted',
  'SEC-ADV-DMARC': 'assisted',
  'SEC-ADV-DKIM': 'manual',
  'SEC-ADV-MTA-STS': 'assisted',
  'SEC-ADV-DNSSEC': 'manual',
  'SEC-ADV-CAA': 'assisted',
  'SEC-ADV-SAFE-BROWSING': 'expert',
  'SEC-ADV-BREACH': 'expert',
};

/**
 * The audit-time "auto" hint for a check: the provider + action kind that fixes it once that provider is connected.
 * Every pair must be implemented by the provider (apps/api providers test asserts it).
 */
export const AUDIT_CHECK_AUTO_ACTIONS: Readonly<Partial<Record<AdvancedCheckId | SecurityAdvCheckId, { provider: RemediationProviderId; actionKind: ActionKind }>>> = {
  'ADV-LLMS-TXT': { provider: 'wordpress', actionKind: 'wordpress.llms_txt.publish' },
  'ADV-A11Y-ALT': { provider: 'wordpress', actionKind: 'wordpress.image_alt.set' },
  'ADV-SOCIAL-HIDDEN': { provider: 'wordpress', actionKind: 'wordpress.social_links.set' },
  'ADV-TRACKING': { provider: 'meta', actionKind: 'meta.pixel.install' },
  'SEC-ADV-HTTPS-REDIRECT': { provider: 'cloudflare', actionKind: 'cloudflare.always_use_https' },
  'SEC-ADV-HSTS': { provider: 'cloudflare', actionKind: 'cloudflare.hsts' },
  'SEC-ADV-CSP': { provider: 'cloudflare', actionKind: 'cloudflare.security_headers' },
  'SEC-ADV-FRAME': { provider: 'cloudflare', actionKind: 'cloudflare.security_headers' },
  'SEC-ADV-NOSNIFF': { provider: 'cloudflare', actionKind: 'cloudflare.security_headers' },
  'SEC-ADV-REFERRER': { provider: 'cloudflare', actionKind: 'cloudflare.security_headers' },
  'SEC-ADV-PERMISSIONS': { provider: 'cloudflare', actionKind: 'cloudflare.security_headers' },
  'SEC-ADV-MIXED-CONTENT': { provider: 'cloudflare', actionKind: 'cloudflare.automatic_https_rewrites' },
  'SEC-ADV-WP-VERSION': { provider: 'wordpress', actionKind: 'wordpress.version.hide' },
  'SEC-ADV-WP-USERS': { provider: 'wordpress', actionKind: 'wordpress.user_enumeration.block' },
  'SEC-ADV-WP-AUTHOR-SCAN': { provider: 'wordpress', actionKind: 'wordpress.user_enumeration.block' },
  'SEC-ADV-WP-XMLRPC': { provider: 'wordpress', actionKind: 'wordpress.xmlrpc.disable' },
  // SPF / DMARC / CAA stay `assisted` at audit time: Cloudflare can write them only after the customer opts in to DNS
  // edit (the provider registry still offers cloudflare.dns_* once connected).
};

/** Audit-time resolvability hint for an ADV / SEC-ADV check (auto when a provider action exists, else the default mode). */
export function auditCheckHint(id: AdvancedCheckId | SecurityAdvCheckId): Resolvability {
  const auto = AUDIT_CHECK_AUTO_ACTIONS[id];
  return auto ? { mode: 'auto', provider: auto.provider, actionKind: auto.actionKind } : { mode: AUDIT_CHECK_DEFAULT_MODES[id] };
}

export const ROADMAP_SLOTS = ['quick_win', 'days_30', 'days_90', 'later'] as const;
export const RoadmapSlotSchema = z.enum(ROADMAP_SLOTS);
export type RoadmapSlot = z.infer<typeof RoadmapSlotSchema>;

export const RISKS = ['low', 'medium', 'high'] as const;
export const RiskSchema = z.enum(RISKS);
export type Risk = z.infer<typeof RiskSchema>;

export const PROPOSAL_STATUSES = ['proposed', 'approved', 'rejected', 'expired', 'applying', 'applied', 'failed', 'verified', 'rolled_back'] as const;
export const ProposalStatusSchema = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;

/** Allowed proposal transitions (engine). Terminal: rejected, expired, rolled_back. */
export const PROPOSAL_TRANSITIONS: Partial<Record<ProposalStatus, readonly ProposalStatus[]>> = {
  proposed: ['approved', 'rejected', 'expired'],
  approved: ['applying', 'expired'],
  applying: ['applied', 'failed'],
  applied: ['verified', 'failed', 'rolled_back'],
  verified: ['rolled_back'],
  failed: ['rolled_back'],
};
export const canTransitionProposal = (from: ProposalStatus, to: ProposalStatus) => PROPOSAL_TRANSITIONS[from]?.includes(to) ?? false;

export const REMEDIATION_EVENT_KINDS = [
  'proposed', 'approved', 'rejected', 'expired', 'apply_started', 'applied', 'apply_failed', 'verified', 'verify_failed',
  'rollback_requested', 'rollback_approved', 'rolled_back', 'rollback_failed',
] as const;
export const RemediationEventKindSchema = z.enum(REMEDIATION_EVENT_KINDS);
export type RemediationEventKind = z.infer<typeof RemediationEventKindSchema>;

export const ACTOR_TYPES = ['user', 'staff', 'partner', 'system', 'api_key'] as const;
export const ActorTypeSchema = z.enum(ACTOR_TYPES);

// ---------- JSON payloads (before/after, provider detail) ----------
export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() => z.union([
  z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema),
]));

// ---------- findings (spec §2) ----------
export const EvidenceItemSchema = z.object({
  checkId: CheckIdSchema,
  pages: z.array(z.string().max(500)).max(50),
  quote: z.string().max(2000).nullable(),
  count: z.number().int().min(0).nullable(),
  capturedAt: IsoDateTimeSchema,
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

export const AdvancedFindingSchema = z.object({
  id: RefIdSchema,
  source: FindingSourceSchema,
  checkIds: z.array(CheckIdSchema).max(30),
  signal: SignalIdSchema,
  severity: SeveritySchema,
  title: LocalizedTextSchema,
  /** Narrative evidence (quotes measured values). */
  evidence: LocalizedTextSchema,
  /** Structured evidence: at least one item per finding (rule 3). */
  evidenceItems: z.array(EvidenceItemSchema).min(1).max(50),
  impact: LocalizedTextSchema,
  fix: LocalizedTextSchema,
  steps: z.array(LocalizedTextSchema).min(1).max(12),
  owner: FixOwnerSchema,
  effort: EffortSchema,
  brainRefs: z.array(z.string().max(200)).max(30),
  /** Regulatory gating (e.g. healthcare advertising rules) - raises priority. */
  regulatory: z.boolean().default(false),
  priority: z.number().int().min(0).max(100),
  roadmapSlot: RoadmapSlotSchema,
  resolvability: ResolvabilitySchema,
  status: FindingStatusSchema,
  statusReason: z.string().max(1000).nullable().default(null),
  statusUpdatedAt: IsoDateTimeSchema.nullable().default(null),
  /**
   * Live ar_finding_state version (0 = never changed), set by the API when it overlays live state; send it as
   * `expectedVersion` on the first PATCH status. Absent in stored reports.
   */
  stateVersion: z.number().int().min(0).optional(),
});
export type AdvancedFinding = z.infer<typeof AdvancedFindingSchema>;
export type AdvancedFindingInput = z.input<typeof AdvancedFindingSchema>;

/** Business impact 0-3 (0 none, 3 revenue/bookings directly). */
export interface PriorityInput { severity: Severity; impact: 0 | 1 | 2 | 3; effort: 'hours' | 'days' | 'weeks'; regulatory: boolean }
const SEVERITY_POINTS: Record<Severity, number> = { critical: 50, high: 36, medium: 22, low: 10 };
const EFFORT_POINTS = { hours: 15, days: 8, weeks: 2 } as const;

/** Reference priority f(severity, business impact, effort, regulatory gating) -> 0..100. Deterministic. */
export function computePriority(p: PriorityInput): number {
  const score = SEVERITY_POINTS[p.severity] + p.impact * 7 + EFFORT_POINTS[p.effort] + (p.regulatory ? 14 : 0);
  return Math.max(0, Math.min(100, score));
}

/** Reference 90-day roadmap slot from priority + effort. */
export function roadmapSlotFor(priority: number, effort: 'hours' | 'days' | 'weeks'): RoadmapSlot {
  if (priority >= 60 && effort === 'hours') return 'quick_win';
  if (priority >= 50) return 'days_30';
  if (priority >= 30) return 'days_90';
  return 'later';
}

/** One status change (immutable history row). */
export const FindingStatusChangeSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema,
  findingId: RefIdSchema,
  from: FindingStatusSchema,
  to: FindingStatusSchema,
  reason: z.string().nullable(),
  actorId: z.string(),
  actorType: ActorTypeSchema,
  actor: z.enum(FINDING_ACTORS),
  proposalId: z.string().nullable(),
  at: IsoDateTimeSchema,
});
export type FindingStatusChange = z.infer<typeof FindingStatusChangeSchema>;

export const FindingStateSchema = z.object({
  jobId: RefIdSchema,
  findingId: RefIdSchema,
  status: FindingStatusSchema,
  reason: z.string().nullable(),
  updatedBy: z.string(),
  updatedAt: IsoDateTimeSchema,
  version: z.number().int().min(1),
});
export type FindingState = z.infer<typeof FindingStateSchema>;

// ---------- advanced report ----------
export const SignalSummarySchema = z.object({
  id: SignalIdSchema,
  score: z.number().int().min(0).max(100).nullable(),
  grade: GradeSchema,
  findingCount: z.number().int().min(0),
});
export type SignalSummary = z.infer<typeof SignalSummarySchema>;

export const RoadmapSchema = z.object({
  quick_win: z.array(RefIdSchema),
  days_30: z.array(RefIdSchema),
  days_90: z.array(RefIdSchema),
  later: z.array(RefIdSchema),
});
export type Roadmap = z.infer<typeof RoadmapSchema>;

export const AdvancedReportSchema = z.object({
  version: z.literal(AUDIT_REMEDIATION_VERSION),
  jobId: RefIdSchema,
  generatedAt: IsoDateTimeSchema,
  /** Audited host or business name, for the heading. */
  subject: z.object({ businessName: z.string().max(200), domain: z.string().max(253).nullable() }),
  findings: z.array(AdvancedFindingSchema).max(500),
  signals: z.array(SignalSummarySchema).max(6),
  coverage: CoverageSchema,
  competitors: z.array(CompetitorAnalysisSchema).max(10),
  roadmap: RoadmapSchema,
});
export type AdvancedReport = z.infer<typeof AdvancedReportSchema>;
export type AdvancedReportInput = z.input<typeof AdvancedReportSchema>;

// ---------- saved report views (customisation) ----------
export const REPORT_SECTIONS = ['summary', 'signals', 'findings', 'roadmap', 'competitors', 'coverage'] as const;
export const FINDING_COLUMNS = ['priority', 'severity', 'signal', 'source', 'status', 'resolvability', 'owner', 'effort', 'roadmapSlot', 'checkIds'] as const;
export const SORT_FIELDS = ['priority', 'severity', 'signal', 'status', 'effort', 'roadmapSlot', 'statusUpdatedAt', 'title'] as const;
export const GROUP_BY = ['none', 'signal', 'severity', 'status', 'resolvability', 'roadmapSlot', 'source', 'owner'] as const;

export const ReportFiltersSchema = z.object({
  severity: z.array(SeveritySchema).max(4).default([]),
  signal: z.array(SignalIdSchema).max(6).default([]),
  source: z.array(FindingSourceSchema).max(5).default([]),
  status: z.array(FindingStatusSchema).max(10).default([]),
  resolvability: z.array(ResolvabilityModeSchema).max(4).default([]),
  owner: z.array(FixOwnerSchema).max(4).default([]),
  roadmapSlot: z.array(RoadmapSlotSchema).max(4).default([]),
  search: z.string().trim().max(200).nullable().default(null),
});
export type ReportFilters = z.infer<typeof ReportFiltersSchema>;

export const ReportViewDefinitionSchema = z.object({
  filters: ReportFiltersSchema.default(() => ReportFiltersSchema.parse({})),
  sort: z.object({ field: z.enum(SORT_FIELDS), dir: z.enum(['asc', 'desc']) }).default({ field: 'priority', dir: 'desc' }),
  groupBy: z.enum(GROUP_BY).default('none'),
  hiddenSections: z.array(z.enum(REPORT_SECTIONS)).max(REPORT_SECTIONS.length).default([]),
  columns: z.array(z.enum(FINDING_COLUMNS)).min(1).max(FINDING_COLUMNS.length).default(['priority', 'severity', 'signal', 'status', 'resolvability']),
});
export type ReportViewDefinition = z.infer<typeof ReportViewDefinitionSchema>;

export const ReportViewSchema = ReportViewDefinitionSchema.extend({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  createdBy: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ReportView = z.infer<typeof ReportViewSchema>;
export const MAX_REPORT_VIEWS = 50;

// ---------- advanced report detail + portal view (track T3; hoisted by the integrator) ----------
// The detail document is written by qm-audit (apps/audit/src/analysis/advanced) to the private EVIDENCE bucket and
// read by qm-api (report/detail.ts); the view is what GET /report/jobs/:jobId serves to the portal. One definition here.

export const ADVANCED_DETAIL_VERSION = 1 as const;
export const ADVANCED_DETAIL_OBJECT = 'advanced-report.v1.json';

export const WeeksSchema = z.enum(['1-2', '3-6', '7-12']);
export type Weeks = z.infer<typeof WeeksSchema>;
export const NotMeasuredItemSchema = z.object({ ref: z.string().max(200), reason: z.string().max(200) });
export type NotMeasuredItem = z.infer<typeof NotMeasuredItemSchema>;
export const CheckFamilySchema = z.enum(['rubric', 'health', 'six_factor', 'signal', 'advanced']);
export type CheckFamily = z.infer<typeof CheckFamilySchema>;

export const AbsorbedCheckSchema = z.object({
  checkId: z.string().max(120),
  source: FindingSourceSchema,
  family: CheckFamilySchema,
  severity: SeveritySchema,
  title: LocalizedTextSchema,
  evidenceItems: z.array(EvidenceItemSchema).max(50),
});
export type AbsorbedCheck = z.infer<typeof AbsorbedCheckSchema>;

export const PriorityBreakdownSchema = z.object({
  severityPoints: z.number().int(),
  impact: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  impactPoints: z.number().int(),
  effortPoints: z.number().int(),
  regulatoryPoints: z.number().int(),
  total: z.number().int().min(0).max(100),
});
export type PriorityBreakdown = z.infer<typeof PriorityBreakdownSchema>;

export const BenchmarkLineSchema = z.object({
  competitor: z.string().max(200),
  domain: z.string().max(253).nullable(),
  signal: SignalIdSchema,
  theirScore: z.number().int().min(0).max(100),
  ourScore: z.number().int().min(0).max(100),
  line: LocalizedTextSchema,
});
export type BenchmarkLine = z.infer<typeof BenchmarkLineSchema>;

export const AdvancedFindingDetailSchema = z.object({
  findingId: z.string(),
  sources: z.array(FindingSourceSchema).max(5),
  weeks: WeeksSchema,
  priority: PriorityBreakdownSchema,
  absorbed: z.array(AbsorbedCheckSchema).max(30),
  benchmarks: z.array(BenchmarkLineSchema).max(10),
  notMeasured: z.array(NotMeasuredItemSchema).max(50),
  measurement: z.boolean(),
  spend: z.boolean(),
  regulatoryBasis: z.array(z.string().max(300)).max(10),
});
export type AdvancedFindingDetail = z.infer<typeof AdvancedFindingDetailSchema>;

const bySignal = <T extends z.ZodTypeAny>(t: T) => z.object(Object.fromEntries(SIGNAL_IDS.map((id) => [id, t])) as Record<SignalId, T>);

export const ExecutiveViewSchema = z.object({
  top5: z.array(z.string()).max(5),
  quickWins: z.array(z.string()).max(10),
  riskExposure: z.object({
    level: z.enum(['high', 'medium', 'low']),
    critical: z.number().int().min(0), high: z.number().int().min(0), medium: z.number().int().min(0), low: z.number().int().min(0),
    regulatory: z.number().int().min(0), security: z.number().int().min(0),
    summary: LocalizedTextSchema,
  }),
  estimatedImpact: z.object({
    overallNow: z.number().nullable(),
    overallAfterWeek6: z.number().nullable(),
    overallAfterWeek12: z.number().nullable(),
    bySignal: bySignal(z.object({ now: z.number().nullable(), after: z.number().nullable() })),
    basis: LocalizedTextSchema,
  }),
});
export type ExecutiveView = z.infer<typeof ExecutiveViewSchema>;

export const AdvancedReportDetailSchema = z.object({
  version: z.literal(ADVANCED_DETAIL_VERSION),
  jobId: z.string(),
  generatedAt: z.string(),
  findings: z.record(z.string(), AdvancedFindingDetailSchema),
  notMeasuredBySignal: bySignal(z.array(NotMeasuredItemSchema).max(50)),
  executive: ExecutiveViewSchema,
  priorityMethod: z.string().max(1000),
});
export type AdvancedReportDetail = z.infer<typeof AdvancedReportDetailSchema>;

/** Live state of one finding (finding_state + customer-visible expert notes + pending proposals). */
export const FindingLiveSchema = z.object({
  /** ar_finding_state version (0 = never changed); send as expectedVersion on PATCH status */
  version: z.number().int().min(0),
  notesCount: z.number().int().min(0),
  /** proposals proposed / approved / applying */
  proposalsPending: z.number().int().min(0),
});
export type FindingLive = z.infer<typeof FindingLiveSchema>;

/** One row of the portal findings table. */
export const AdvancedFindingRowSchema = AdvancedFindingSchema.extend({
  live: FindingLiveSchema,
  weeks: WeeksSchema,
  sources: z.array(FindingSourceSchema).max(5),
  absorbedCount: z.number().int().min(0),
  benchmarkCount: z.number().int().min(0),
});
export type AdvancedFindingRow = z.infer<typeof AdvancedFindingRowSchema>;

export const ReportGroupSchema = z.object({ key: z.string(), findingIds: z.array(z.string()) });
export type ReportGroup = z.infer<typeof ReportGroupSchema>;

/** GET /report/jobs/:jobId -> { data: AdvancedReportView } */
export const AdvancedReportViewSchema = z.object({
  jobId: z.string(),
  generatedAt: z.string(),
  subject: AdvancedReportSchema.shape.subject,
  signals: AdvancedReportSchema.shape.signals,
  coverage: AdvancedReportSchema.shape.coverage,
  competitors: AdvancedReportSchema.shape.competitors,
  roadmap: AdvancedReportSchema.shape.roadmap,
  executive: ExecutiveViewSchema.nullable(),
  notMeasuredBySignal: AdvancedReportDetailSchema.shape.notMeasuredBySignal.nullable(),
  priorityMethod: z.string().nullable(),
  findings: z.array(AdvancedFindingRowSchema),
  groups: z.array(ReportGroupSchema).nullable(),
  totals: z.object({ all: z.number().int(), filtered: z.number().int(), byStatus: z.partialRecord(FindingStatusSchema, z.number().int()) }),
  query: z.object({ filters: ReportFiltersSchema, sort: ReportViewDefinitionSchema.shape.sort.unwrap(), groupBy: z.enum(GROUP_BY) }),
});
export type AdvancedReportView = z.infer<typeof AdvancedReportViewSchema>;

/** GET /report/jobs/:jobId/findings/:findingId -> { data: AdvancedFindingView } */
export const AdvancedFindingViewSchema = z.object({
  finding: AdvancedFindingRowSchema,
  detail: AdvancedFindingDetailSchema.nullable(),
  history: z.array(FindingStatusChangeSchema),
  proposals: z.array(z.object({
    id: z.string(), provider: z.string(), actionKind: z.string(), status: z.string(), risk: z.string(), reversible: z.boolean(), createdAt: z.string(), updatedAt: z.string(),
  })),
});
export type AdvancedFindingView = z.infer<typeof AdvancedFindingViewSchema>;
export const GetAdvancedReportViewResponseSchema = z.object({ data: AdvancedReportViewSchema });
export const GetAdvancedFindingViewResponseSchema = z.object({ data: AdvancedFindingViewSchema });

// ---------- remediation proposals ----------
export const RemediationTargetSchema = z.object({
  /** e.g. 'page', 'post', 'location', 'zone_setting', 'pixel' */
  kind: z.string().min(1).max(40),
  /** Provider-side reference (URL, post id, location name, zone id). Never a secret. */
  ref: z.string().min(1).max(500),
  label: z.string().max(200).nullable().default(null),
});
export type RemediationTarget = z.infer<typeof RemediationTargetSchema>;

export const RemediationProposalSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema,
  findingId: RefIdSchema,
  provider: RemediationProviderIdSchema,
  actionKind: ActionKindSchema,
  connectionId: z.string(),
  target: RemediationTargetSchema,
  before: JsonValueSchema,
  after: JsonValueSchema,
  /** Human-readable unified diff shown before approval. */
  diff: z.string().max(100_000),
  risk: RiskSchema,
  reversible: z.boolean(),
  expiresAt: IsoDateTimeSchema,
  status: ProposalStatusSchema,
  proposedBy: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: IsoDateTimeSchema.nullable(),
  appliedAt: IsoDateTimeSchema.nullable(),
  verifiedAt: IsoDateTimeSchema.nullable(),
  providerRef: z.string().nullable(),
  failureCode: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type RemediationProposal = z.infer<typeof RemediationProposalSchema>;

/** Immutable history of a proposal (append-only table). */
export const RemediationEventSchema = z.object({
  id: z.string(),
  proposalId: z.string(),
  findingId: RefIdSchema,
  kind: RemediationEventKindSchema,
  actorId: z.string(),
  actorType: ActorTypeSchema,
  /** Non-secret detail (provider ref, error code, check result). */
  detail: z.record(z.string(), JsonValueSchema),
  at: IsoDateTimeSchema,
});
export type RemediationEvent = z.infer<typeof RemediationEventSchema>;

// ---------- provider connections (metadata only) ----------
export const CONNECTION_STATUSES = ['connected', 'needs_reauth', 'error', 'revoked'] as const;
export const ConnectionStatusSchema = z.enum(CONNECTION_STATUSES);
export const ProviderConnectionSchema = z.object({
  id: z.string(),
  provider: RemediationProviderIdSchema,
  status: ConnectionStatusSchema,
  scopes: z.array(z.string().max(200)).max(50),
  accountLabel: z.string().max(200),
  /** Non-secret account reference (site URL, location name, zone name, ad account id). */
  accountRef: z.string().max(500),
  connectedBy: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  lastCheckedAt: IsoDateTimeSchema.nullable(),
  lastErrorCode: z.string().nullable(),
}).strict();
export type ProviderConnection = z.infer<typeof ProviderConnectionSchema>;

// ---------- expert ----------
export const ExpertNoteSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema,
  findingId: RefIdSchema.nullable(),
  authorId: z.string(),
  body: z.string().min(1).max(5000),
  visibility: z.enum(['internal', 'customer']),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ExpertNote = z.infer<typeof ExpertNoteSchema>;

export const ExpertSignoffSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema,
  reviewerId: z.string(),
  decision: z.enum(['approved', 'changes_requested']),
  summary: LocalizedTextSchema,
  findingIds: z.array(RefIdSchema).max(500),
  signedAt: IsoDateTimeSchema,
});
export type ExpertSignoff = z.infer<typeof ExpertSignoffSchema>;

export const BOOKING_STATUSES = ['requested', 'payment_pending', 'confirmed', 'completed', 'cancelled'] as const;
export const ExpertBookingSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema.nullable(),
  findingId: RefIdSchema.nullable(),
  topic: z.string().min(3).max(500),
  status: z.enum(BOOKING_STATUSES),
  scheduledAt: IsoDateTimeSchema.nullable(),
  durationMin: z.number().int().min(15).max(240),
  priceMinor: z.number().int().min(0),
  currency: z.string().length(3),
  paymentRef: z.string().nullable(),
  expertId: z.string().nullable(),
  requestedBy: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ExpertBooking = z.infer<typeof ExpertBookingSchema>;

// ---------- advisor ----------
export const AdvisorThreadSchema = z.object({
  id: z.string(),
  jobId: RefIdSchema.nullable(),
  findingId: RefIdSchema.nullable(),
  title: z.string().max(200),
  createdBy: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type AdvisorThread = z.infer<typeof AdvisorThreadSchema>;

export const AdvisorMessageSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  role: z.enum(['user', 'advisor', 'expert']),
  authorId: z.string().nullable(),
  body: z.string().min(1).max(8000),
  /** Finding ids / check ids / brain refs the answer is grounded in. */
  citations: z.array(z.string().max(200)).max(30),
  createdAt: IsoDateTimeSchema,
});
export type AdvisorMessage = z.infer<typeof AdvisorMessageSchema>;

// ---------- monitoring ----------
export const MonitorScheduleSchema = z.object({
  id: z.string(),
  baselineJobId: RefIdSchema,
  cadence: z.enum(['daily', 'weekly', 'monthly']),
  /** empty = every check of the baseline run */
  checkIds: z.array(CheckIdSchema).max(200),
  enabled: z.boolean(),
  nextRunAt: IsoDateTimeSchema.nullable(),
  lastRunAt: IsoDateTimeSchema.nullable(),
  createdBy: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type MonitorSchedule = z.infer<typeof MonitorScheduleSchema>;

export const MonitorRunSchema = z.object({
  id: z.string(),
  scheduleId: z.string(),
  status: z.enum(['queued', 'running', 'succeeded', 'failed']),
  startedAt: IsoDateTimeSchema.nullable(),
  finishedAt: IsoDateTimeSchema.nullable(),
  regressions: z.number().int().min(0),
  resolved: z.number().int().min(0),
  errorCode: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type MonitorRun = z.infer<typeof MonitorRunSchema>;

export const MonitorAlertSchema = z.object({
  id: z.string(),
  runId: z.string(),
  findingId: RefIdSchema.nullable(),
  checkId: CheckIdSchema,
  kind: z.enum(['regression', 'new_issue', 'resolved', 'provider_disconnected']),
  severity: SeveritySchema,
  status: z.enum(['open', 'acknowledged', 'resolved']),
  createdAt: IsoDateTimeSchema,
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: IsoDateTimeSchema.nullable(),
});
export type MonitorAlert = z.infer<typeof MonitorAlertSchema>;

// ---------- security settings ----------
export const APPROVER_ROLES = ['customer_owner', 'customer_admin'] as const;
export const SecuritySettingsSchema = z.object({
  /** Roles that may approve remediation proposals and rollbacks. */
  approverRoles: z.array(z.enum(APPROVER_ROLES)).min(1).max(2),
  /** MFA step-up required before approving (default true). */
  stepUpRequired: z.boolean(),
  /** How recent the step-up must be. */
  stepUpMaxAgeSec: z.number().int().min(60).max(3600),
  updatedBy: z.string().nullable(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type SecuritySettings = z.infer<typeof SecuritySettingsSchema>;
export const DEFAULT_SECURITY_SETTINGS: SecuritySettings = {
  approverRoles: ['customer_owner', 'customer_admin'], stepUpRequired: true, stepUpMaxAgeSec: 600, updatedBy: null, updatedAt: null,
};

// ================= request / response schemas =================

// GET /v1/audit-remediation/jobs/:jobId/report
export const GetAdvancedReportResponseSchema = z.object({ data: AdvancedReportSchema });

// PATCH /v1/audit-remediation/jobs/:jobId/findings/:findingId/status
export const UpdateFindingStatusRequestSchema = z.object({
  status: CustomerSettableStatusSchema,
  reason: z.string().trim().max(1000).optional(),
  /** Optimistic concurrency: the version the client last saw (409 on mismatch). */
  expectedVersion: z.number().int().min(0).optional(),
}).superRefine((b, ctx) => {
  if (REASON_REQUIRED_STATUSES.includes(b.status) && (!b.reason || b.reason.length < 5)) {
    ctx.addIssue({ code: 'custom', path: ['reason'], message: 'A reason (at least 5 characters) is required' });
  }
});
export type UpdateFindingStatusRequest = z.infer<typeof UpdateFindingStatusRequestSchema>;
export const UpdateFindingStatusResponseSchema = z.object({ data: FindingStateSchema });

// GET /v1/audit-remediation/jobs/:jobId/findings/:findingId/history
export const FindingHistoryResponseSchema = z.object({ data: z.array(FindingStatusChangeSchema) });

// /v1/audit-remediation/views
export const CreateReportViewRequestSchema = ReportViewDefinitionSchema.extend({
  name: z.string().trim().min(1).max(80),
  isDefault: z.boolean().default(false),
});
export type CreateReportViewRequest = z.input<typeof CreateReportViewRequestSchema>;
export const UpdateReportViewRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  isDefault: z.boolean().optional(),
  filters: ReportFiltersSchema.optional(),
  sort: z.object({ field: z.enum(SORT_FIELDS), dir: z.enum(['asc', 'desc']) }).optional(),
  groupBy: z.enum(GROUP_BY).optional(),
  hiddenSections: z.array(z.enum(REPORT_SECTIONS)).max(REPORT_SECTIONS.length).optional(),
  columns: z.array(z.enum(FINDING_COLUMNS)).min(1).max(FINDING_COLUMNS.length).optional(),
});
export type UpdateReportViewRequest = z.infer<typeof UpdateReportViewRequestSchema>;
export const ReportViewResponseSchema = z.object({ data: ReportViewSchema });
export const ListReportViewsResponseSchema = z.object({ data: z.array(ReportViewSchema) });

// GET /v1/audit-remediation/providers/connections
export const ListProviderConnectionsResponseSchema = z.object({ data: z.array(ProviderConnectionSchema) });

// ---- for tracks (engine, advisor, expert, security, monitoring); shapes agreed up front ----
export const CreateProposalRequestSchema = z.object({
  jobId: RefIdSchema,
  findingId: RefIdSchema,
  provider: RemediationProviderIdSchema,
  actionKind: ActionKindSchema,
  connectionId: z.string().min(1).max(64),
  /** Optional customer-supplied content for assisted fixes turned auto (e.g. a new meta description). */
  input: z.record(z.string(), JsonValueSchema).optional(),
});
export type CreateProposalRequest = z.infer<typeof CreateProposalRequestSchema>;
export const ProposalDecisionRequestSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().max(1000).optional(),
});
export type ProposalDecisionRequest = z.infer<typeof ProposalDecisionRequestSchema>;
export const ProposalResponseSchema = z.object({ data: RemediationProposalSchema });

export const AdvisorAskRequestSchema = z.object({
  threadId: z.string().max(64).optional(),
  jobId: RefIdSchema.optional(),
  findingId: RefIdSchema.optional(),
  question: z.string().trim().min(2).max(2000),
  locale: z.enum(['en', 'ar']).default('en'),
});
export type AdvisorAskRequest = z.infer<typeof AdvisorAskRequestSchema>;

export const CreateExpertBookingRequestSchema = z.object({
  jobId: RefIdSchema.optional(),
  findingId: RefIdSchema.optional(),
  topic: z.string().trim().min(3).max(500),
  durationMin: z.union([z.literal(30), z.literal(60)]),
  preferredSlots: z.array(IsoDateTimeSchema).min(1).max(5),
});
export type CreateExpertBookingRequest = z.infer<typeof CreateExpertBookingRequestSchema>;

export const CreateExpertNoteRequestSchema = z.object({
  findingId: RefIdSchema.nullable().default(null),
  body: z.string().trim().min(1).max(5000),
  visibility: z.enum(['internal', 'customer']).default('internal'),
});
export type CreateExpertNoteRequest = z.infer<typeof CreateExpertNoteRequestSchema>;

export const UpdateSecuritySettingsRequestSchema = z.object({
  approverRoles: z.array(z.enum(APPROVER_ROLES)).min(1).max(2).optional(),
  stepUpRequired: z.boolean().optional(),
  stepUpMaxAgeSec: z.number().int().min(60).max(3600).optional(),
});
export type UpdateSecuritySettingsRequest = z.infer<typeof UpdateSecuritySettingsRequestSchema>;

export const UpsertMonitorScheduleRequestSchema = z.object({
  baselineJobId: RefIdSchema,
  cadence: z.enum(['daily', 'weekly', 'monthly']),
  checkIds: z.array(CheckIdSchema).max(200).default([]),
  enabled: z.boolean().default(true),
});
export type UpsertMonitorScheduleRequest = z.infer<typeof UpsertMonitorScheduleRequestSchema>;
